import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import TextField from 'src/components/TextField';
import SelectBox from 'src/components/SelectBox';
import Form, { FormRow } from 'src/components/Form';
import Button from 'src/components/Button';
import CloneButton from 'src/components/CloneButton';
import DeleteButton from 'src/components/DeleteButton';
import RestoreButton from 'src/components/RestoreButton';
import Checkbox from 'src/components/Checkbox';
import CurrentUserContext from 'utils/context/CurrentUserContext';
import { asOptions } from 'src/utils/core/array';
import { useTranslation } from 'react-i18next';

const UserForm = props => {
  const {
    values,
    errors,
    isSubmitting,
    handleChange,
    handleBlur,
    handleSubmit,
    setFieldValue,
    className,
    roles,
    filteringRuleGroups,
    timezones,
    isDeleted,
  } = props;

  const { t } = useTranslation();

  const currentUser = useContext(CurrentUserContext);

  const rolesOptions = roles
    .filter(v => !v.deletedAt || (values.roles || []).map(c => c.value).includes(v.id))
    .map(r => ({
      value: r.id,
      label: r.name,
      isDisabled: !!r.deletedAt,
    }));

  const filteringRuleGroupsOptions = filteringRuleGroups
    .filter(v => !v.deletedAt || (values.filteringRuleGroups || []).map(c => c.value).includes(v.id))
    .map(r => ({
      value: r.id,
      label: r.name,
      isDisabled: !!r.deletedAt,
    }));

  const timezonesOptions = asOptions(timezones);
  const timezoneValue = timezonesOptions.find(
    v => v.value === values.timezoneId,
  );

  return (
    <Form className={className} onSubmit={handleSubmit}>
      <FormRow>
        <TextField
          error={errors.firstName}
          id="firstName"
          label={t('FORM_FIRST_NAME')}
          onBlur={handleBlur}
          onChange={handleChange}
          value={values.firstName}
        />
        <TextField
          error={errors.lastName}
          id="lastName"
          label={t('FORM_LAST_NAME')}
          onBlur={handleBlur}
          onChange={handleChange}
          value={values.lastName}
        />
        <TextField
          error={errors.email}
          id="email"
          label={t('FORM_EMAIL')}
          onBlur={handleBlur}
          onChange={handleChange}
          value={values.email}
        />
        <TextField
          error={errors.login}
          id="login"
          label={t('FORM_LOGIN')}
          onBlur={handleBlur}
          onChange={handleChange}
          value={values.login}
        />        
        <Checkbox
            id="usersThirdPartyCheckbox"
            className="removeLeftPadding"
            label={t('THIRD_PARTY_INTERNAL_USERS')}
            onBlur={handleBlur}  
            onChange={e => setFieldValue('isThirdPartyUser', e.target.checked)}
            value={values.isThirdPartyUser}
          />
      </FormRow>

      <FormRow>
        <SelectBox
          error={errors.roles}
          id="role"
          isMulti
          label={t('FORM_ROLE')}
          onBlur={handleBlur}
          onChange={value => setFieldValue('roles', value)}
          options={rolesOptions}
          value={values.roles}
        />
        <SelectBox
          error={errors.filteringRuleGroups}
          id="filteringRuleGroups"
          isClearable
          isMulti
          label={t('FORM_FILTERING_RULES')}
          onBlur={handleBlur}
          onChange={value => setFieldValue('filteringRuleGroups', value || [])}
          options={filteringRuleGroupsOptions}
          value={values.filteringRuleGroups}
        />
      </FormRow>

      <FormRow>
        <SelectBox
          error={errors.timezoneId}
          id="timezone_id"
          label={t('FORM_TIMEZONE')}
          onBlur={handleBlur}
          onChange={value => setFieldValue('timezoneId', value.value)}
          options={timezonesOptions}
          value={timezoneValue}
        />
        {
          values.desktopVersion && (<TextField
            id="desktop_app_version"
            label={t('DESKTOP_APP_VERSION')}
            value={values.desktopVersion}
          />)
        }
        {
          values.mobileAppVersion && (<TextField
            id="mobile_app_version"
            label={t('Mobile_App_Version')}
            value={values.mobileAppVersion}
          />)
        }
        {
          values.mobileDeviceInfo && (<TextField
            id="mobile_device_info"
            label={t('Mobile_Device_Info')}
            value={values.mobileDeviceInfo}
          />)
        }
        {(currentUser.id !== values.id) && (
          <Checkbox
            id="active"
            label={t('FORM_ACTIVE')}
            onBlur={handleBlur}
            onChange={handleChange}
            value={values.active}
          />
        )}
      </FormRow>

      {!isDeleted ? (
        <FormRow inline spaced>
          <Button disabled={isSubmitting} orange sm solid type="submit">
            {isSubmitting ? t('PLEASE_WAIT') : t('SAVE_USER')}
          </Button>
          <div>
            {values.id && <CloneButton values={values} />}
            {values.id && <DeleteButton id={values.id} resourceName="User" />}
          </div>
        </FormRow>
      ) : (
        <FormRow inline spaced>
          {values.id && <RestoreButton id={values.id} resourceName="User" />}
        </FormRow>
      )}
    </Form>
  );
};

UserForm.propTypes = {
  values: PropTypes.shape({
    id: PropTypes.number,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    login: PropTypes.string,
    desktopVersion: PropTypes.string,
    roles: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        label: PropTypes.string,
      }),
    ),
    filteringRuleGroups: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        label: PropTypes.string,
      }),
    ),
    timezoneId: PropTypes.number,
    email: PropTypes.string,
    active: PropTypes.bool,
    isThirdPartyUser: PropTypes.bool,
  }).isRequired,
  touched: PropTypes.shape({}).isRequired,
  errors: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    login: PropTypes.string,
    desktopVersion: PropTypes.string,
    roles: PropTypes.string,
    filteringRuleGroups: PropTypes.string,
    timezoneId: PropTypes.string,
    email: PropTypes.string,
    active: PropTypes.string,
  }).isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  handleChange: PropTypes.func.isRequired,
  handleBlur: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  className: PropTypes.string,
  roles: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  filteringRuleGroups: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  timezones: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  isDeleted: PropTypes.bool,
};

UserForm.defaultProps = {
  className: null,
  role: null,
};

export default UserForm;
